import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Section from "../components/shared/Section";
import { galleryImages } from "../data/gallary";

export default function Gallery() {
  const [startIndex, setStartIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3); // Default to desktop

  // 📱 Dynamically set visibleCount based on screen width
  useEffect(() => {
    const updateVisibleCount = () => {
      const width = window.innerWidth;

      if (width < 640) {
        setVisibleCount(1); // Mobile
      } else if (width < 768) {
        setVisibleCount(2); // Small tablets / phablets
      } else {
        setVisibleCount(3); // Tablets and desktops
      }
    };

    updateVisibleCount(); // Set initially
    window.addEventListener("resize", updateVisibleCount); // Watch for resize

    return () => window.removeEventListener("resize", updateVisibleCount); // Cleanup
  }, []);

  const canGoNext = startIndex + visibleCount < galleryImages.length;
  const canGoPrev = startIndex > 0;

  const handleNext = () => {
    if (canGoNext) setStartIndex((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (canGoPrev) setStartIndex((prev) => prev - 1);
  };

  const visibleImages = galleryImages.slice(
    startIndex,
    startIndex + visibleCount
  );

  return (
    <Section id="applications">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-10">
        {/* Heading */}
        <div className="text-center">
          <h2 className="text-2xl sm:text-4xl font-semibold text-center drop-shadow-[0_1px_8px_rgba(255,200,0,0.15)]">
            Bringing Beauty <span className="text-yellow-700"> to Life</span>
          </h2>
        </div>

        {/* Arrows */}
        <div className="flex justify-end">
          <div className="flex gap-3">
            <button
              onClick={handlePrev}
              disabled={!canGoPrev}
              className="group rounded-full bg-black border border-[#cd7f32] p-2 w-10 h-10 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition hover:bg-[#cd7f32]/10"
              aria-label="Previous"
            >
              <span className="text-2xl font-semibold text-[#cd7f32] group-hover:scale-110 transition-transform">
                ‹
              </span>
            </button>

            <button
              onClick={handleNext}
              disabled={!canGoNext}
              className="group rounded-full bg-black border border-[#cd7f32] p-2 w-10 h-10 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition hover:bg-[#cd7f32]/10"
              aria-label="Next"
            >
              <span className="text-2xl font-semibold text-[#cd7f32] group-hover:scale-110 transition-transform">
                ›
              </span>
            </button>
          </div>
        </div>

        {/* Image Grid */}
        <div
          className={`grid gap-6 transition-all duration-500 ${
            visibleCount === 1
              ? "grid-cols-1"
              : visibleCount === 2
              ? "sm:grid-cols-2"
              : "sm:grid-cols-2 md:grid-cols-3"
          }`}
        >
          <AnimatePresence mode="popLayout">
            {visibleImages.map((src, idx) => (
              <motion.div
                key={src}
                layout
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.5 }}
                className="overflow-hidden rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm"
              >
                <img
                  src={src}
                  alt={`Gallery ${idx}`}
                  className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </Section>
  );
}
